import * as React from 'react'
import * as Sentry from '@sentry/nextjs'
import type { AlgoliaSearchedProductsLocalStorage } from 'algolia/algolia'
import { AlgoliaEventName, algoliaAnalytics } from 'algolia/algolia'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { googleTrackProductDownloadClick } from '@repo/analytics/google-site-tag'
import { isLoggedInSelector } from 'auth/selectors'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { useStartFreeTrial } from 'payments/start-free-trial-button/hooks/use-start-free-trial'
import {
  useUserId,
  useHasAllAccessQuery,
  useUser,
  useUserIsEligibleForAllAccessFreeTrialQuery,
  useUserIsEligibleForAllAccessYearlyUpgradeQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { localStore } from 'utils/storage'

import { useContentIncreaseDownloadCountMutation } from '@web/gen/content'
import { useContentProductDataByIdQuery } from '@web/hooks/use-product-data'
import { getIsProductFree } from '@web/components/content-api/config'
import { downloadAsset, getAssetUrl } from '@web/components/prod/helpers'
import { PaymentStatus } from 'codegen/types'

function mapContentPaymentStatusToPaymentErrorStatus(status: string): PaymentStatus {
  switch (status) {
    case 'PAYMENTSTATUS_SUCCEEDED': {
      return PaymentStatus.Succeeded
    }
    case 'PAYMENTSTATUS_PROCESSING': {
      return PaymentStatus.Processing
    }
    case 'PAYMENTSTATUS_REQUIRES_ACTION': {
      return PaymentStatus.RequiresAction
    }
    default: {
      return PaymentStatus.Failed
    }
  }
}

export function useDownloadContentProduct(
  productId: string,
  options?: {
    enabled: boolean
  },
) {
  const [isDownloadingAssets, setIsDownloadingAssets] = React.useState(false)
  const { data: productData } = useContentProductDataByIdQuery(productId, {
    enabled: options?.enabled || false,
  })
  const contentIncreaseDownloadCountMutation = useContentIncreaseDownloadCountMutation({
    retry: 3,
    retryDelay: attempt => attempt * 1000,
  })
  const { data: userData } = useUser()
  const userId = useUserId()
  const { data: userHasAllAccess } = useHasAllAccessQuery()
  const { data: isEligibleForAllAccessFreeTrial } = useUserIsEligibleForAllAccessFreeTrialQuery()
  const { data: isEligibleForAllAccessYearlyUpgrade } =
    useUserIsEligibleForAllAccessYearlyUpgradeQuery()
  const isLoggedIn = isLoggedInSelector({ user: userData })
  const { open: openPayments } = usePaymentsDialog()
  const { startFreeTrial } = useStartFreeTrial()
  const [payment, setPayment] = React.useState<{
    status: PaymentStatus
    paymentUrl?: string
  } | null>(null)

  const isFreeProduct = getIsProductFree(productData?.category.root?.id)

  // oxlint-disable-next-line complexity
  const handleProductDownload = async ({
    assetId,
    onSuccess,
  }: {
    assetId?: string
    onSuccess?: () => void
  }) => {
    if (!isLoggedIn) {
      return
    }

    //If not a free product and eligible for free trial, open the payments dialog with free trial option
    if (!isFreeProduct && !userHasAllAccess && isEligibleForAllAccessFreeTrial) {
      await startFreeTrial()
      return
    }

    //If not a free product and not eligible for free trial but eligible for yearly upgrade, open the payments dialog
    if (!isFreeProduct && !userHasAllAccess && isEligibleForAllAccessYearlyUpgrade) {
      openPayments('yearly')
      return
    }

    //At this point whether the product is free or the user has All Access and we can start the download process
    setIsDownloadingAssets(true)

    try {
      // Sort downloadable assets by size in descending order
      const downloadableAssets = productData?.assets
        .filter(asset => asset.downloadable)
        .toSorted((a, b) => b.size_bytes - a.size_bytes)

      if (downloadableAssets === undefined || downloadableAssets.length === 0) {
        Sentry.captureException(`No asset to download found for productId: ${productId}`)
        return
      }

      //if a specific assetId is provided, use that asset, otherwise use the first asset
      const selectedAsset = assetId
        ? downloadableAssets.find(asset => asset.id === assetId)
        : downloadableAssets[0]

      if (!selectedAsset) {
        Sentry.captureException(`No asset to download found for productId: ${productId}`)
        return
      }

      const algoliaSearchedProducts: Partial<AlgoliaSearchedProductsLocalStorage> = JSON.parse(
        localStore.getString(`algoliaSearchedProducts.${userId || 'guest'}`) || '{}',
      )
      // Tracking event for Amplitude
      sendAmplitudeEvent(AmplitudeEvents.DOWNLOADED_PRODUCT, {
        category: productData?.category.root?.name || '',
        subcategory: productData?.category ? [productData.category.name] : [],
        image_url: selectedAsset?.file_path || '',
        name: productData?.title || '',
        product_id: productId,
        productId,
        type: 'subscription',
        download_origin: 'product_card',
      })

      googleTrackProductDownloadClick(`${productData?.category?.name} - Regular`)

      let productWasSearched = false
      let queryId = ''
      let index = ''

      if (algoliaSearchedProducts) {
        const items = algoliaSearchedProducts.items || {}
        Object.keys(items).forEach(key => {
          // oxlint-disable-next-line unicorn/prefer-includes
          if (Object.keys(items[key]).indexOf(productId) !== -1) {
            index = key
            productWasSearched = true
            queryId = items[key][productId]
          }
        })
      }

      // Tracking event for Algolia
      if (productWasSearched) {
        algoliaAnalytics('convertedObjectIDsAfterSearch', {
          index: index,
          eventName: AlgoliaEventName.PRODUCT_DOWNLOADED,
          objectIDs: [productId],
          queryID: queryId,
        })
      } else {
        algoliaAnalytics('convertedObjectIDs', {
          index: process.env.NEXT_PUBLIC_CONTENT_ALGOLIA_INDEX ?? '',
          eventName: AlgoliaEventName.PRODUCT_DOWNLOADED,
          objectIDs: [productId],
        })
      }

      const { href, download, payment } = await getAssetUrl(
        selectedAsset.id,
        selectedAsset.file_path,
      )

      // oxlint-disable-next-line no-negated-condition
      if (typeof payment !== 'boolean') {
        setPayment({
          status: mapContentPaymentStatusToPaymentErrorStatus(payment.status),
          paymentUrl: payment.paymentUrl,
        })
      } else {
        downloadAsset(href, download)
      }
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      setIsDownloadingAssets(false)
    }

    // Increase the download count for the product only if it's not a free product
    if (!isFreeProduct) {
      await contentIncreaseDownloadCountMutation.mutateAsync(
        {
          productId,
        },
        {
          onError(e) {
            Sentry.captureException(
              new Error(
                `Error while increasing download count for product ${productId}:
                  error = ${(e as Error)?.message ? (e as Error)?.message : (e as string)}`,
              ),
            )
          },
        },
      )
    }

    //if a callback is provided, call it after the download is complete and the download count is increased
    //this can be used to refetch any data that depends on the download count
    if (onSuccess) {
      onSuccess()
    }
  }

  const resetPayment = () => {
    setPayment(null)
  }

  return {
    isLoading: isDownloadingAssets,
    download: handleProductDownload,
    payment,
    resetPayment,
  }
}
